import { z } from 'zod';
import {
  BaseProcessingScheduleSchema,
  ProcessingScheduleSchemaValidation,
} from '../ProcessingScheduleSchema';

export const JiraConfigSchema = z.object({
  server: z.string().optional(),
  project: z.string().optional(),
  issueType: z.string().optional(),
  summary: z.string().optional(),
  email: z.string().optional(),
  token: z.object({
    value: z.string().optional(),
    key: z.string().optional(),
    scope: z.string().optional(),
  }),
  tokenType: z.enum(['token', 'secret']),
});

export const ServiceNowConfigSchema = z.object({
  server: z.string().optional(),
  sourceExpr: z.string().optional(),
  nodeExpr: z.string().optional(),
  severityExpr: z.string().optional(),
  eventType: z.string().optional(),
  userID: z.string().optional(),
  password: z.object({
    value: z.string().optional(),
    scope: z.string().optional(),
    key: z.string().optional(),
  }),
  passwordType: z.enum(['literal', 'secret']),
});

export const WorkspaceConfigAlertSchema = z
  .object({
    destination: z.enum(['none', 'slack', 'webhook', 'jira', 'servicenow']),
    webhookOutputUrlType: z.enum(['url', 'secret']),
    webhookUrl: z.string().optional(),
    webhookScope: z.string().optional(),
    webhookKey: z.string().optional(),
    slackOAuthType: z.enum(['token', 'secret']),
    slackChannel: z.string().optional(),
    slackMessage: z.string().optional(),
    slackTokenValue: z.string().optional(),
    slackTokenKey: z.string().optional(),
    slackTokenScope: z.string().optional(),
    jira: JiraConfigSchema,
    serviceNowConfig: ServiceNowConfigSchema,
  })
  .superRefine((data, ctx) => {
    if (data.destination === 'jira') {
      if (!data.jira.email) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Email is required',
          path: ['jira', 'email'],
        });
      }
      if (!data.jira.project) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Project is required',
          path: ['jira', 'project'],
        });
      }
      if (!data.jira.issueType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Issue type is required',
          path: ['jira', 'issueType'],
        });
      }
      if (!data.jira.summary) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Summary is required',
          path: ['jira', 'summary'],
        });
      }
      if (!data.jira.server) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Server is required',
          path: ['jira', 'server'],
        });
      }
      if (data.jira.tokenType === 'token' && !data.jira.token.value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Token is required',
          path: ['jira', 'token', 'value'],
        });
      }
      if (data.jira.tokenType === 'secret' && !data.jira.token.key) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Secret key is required',
          path: ['jira', 'token', 'key'],
        });
      }
      if (data.jira.tokenType === 'secret' && !data.jira.token.scope) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Secret scope is required',
          path: ['jira', 'token', 'scope'],
        });
      }
    }

    if (data.destination === 'servicenow') {
      if (!data.serviceNowConfig.userID) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'User ID is required',
          path: ['serviceNowConfig', 'userID'],
        });
      }

      if (
        data.serviceNowConfig.passwordType === 'literal' &&
        !data.serviceNowConfig.password.value
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Password is required',
          path: ['serviceNowConfig', 'password', 'value'],
        });
      }

      if (
        data.serviceNowConfig.passwordType === 'secret' &&
        !data.serviceNowConfig.password.key
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Secret key is required',
          path: ['serviceNowConfig', 'password', 'key'],
        });
      }

      if (
        data.serviceNowConfig.passwordType === 'secret' &&
        !data.serviceNowConfig.password.scope
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Secret scope is required',
          path: ['serviceNowConfig', 'password', 'scope'],
        });
      }

      if (!data.serviceNowConfig.server) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Server is required',
          path: ['serviceNowConfig', 'server'],
        });
      }

      if (!data.serviceNowConfig.severityExpr) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Severity expression is required',
          path: ['serviceNowConfig', 'severityExpr'],
        });
      }
    }
  })
  .refine(
    (data) =>
      !(
        data.destination === 'webhook' &&
        data.webhookOutputUrlType === 'url' &&
        !data.webhookUrl
      ),
    {
      message: 'Webhook URL is required',
      path: ['webhookUrl'],
    },
  )
  .refine(
    (data) =>
      !(
        data.destination === 'webhook' &&
        data.webhookOutputUrlType === 'secret' &&
        !data.webhookScope
      ),
    {
      message: 'Webhook scope is required',
      path: ['webhookScope'],
    },
  )
  .refine(
    (data) =>
      !(
        data.destination === 'webhook' &&
        data.webhookOutputUrlType === 'secret' &&
        !data.webhookKey
      ),
    {
      message: 'Webhook key is required',
      path: ['webhookKey'],
    },
  )
  .refine((data) => !(data.destination === 'slack' && !data.slackChannel), {
    message: 'Slack channel is required',
    path: ['slackChannel'],
  })
  .refine((data) => !(data.destination === 'slack' && !data.slackMessage), {
    message: 'Slack message is required',
    path: ['slackMessage'],
  })
  .refine(
    (data) =>
      !(
        data.destination === 'slack' &&
        data.slackOAuthType === 'token' &&
        !data.slackTokenValue
      ),
    {
      message: 'Slack token value is required',
      path: ['slackTokenValue'],
    },
  )
  .refine(
    (data) =>
      !(
        data.destination === 'slack' &&
        data.slackOAuthType === 'secret' &&
        !data.slackTokenKey
      ),
    {
      message: 'Slack token key is required',
      path: ['slackTokenKey'],
    },
  )
  .refine(
    (data) =>
      !(
        data.destination === 'slack' &&
        data.slackOAuthType === 'secret' &&
        !data.slackTokenScope
      ),
    {
      message: 'Slack token scope is required',
      path: ['slackTokenScope'],
    },
  );

export type WorkspaceConfigAlertSchemaValues = z.infer<
  typeof WorkspaceConfigAlertSchema
>;

export const WorkspaceConfigAlertsSchema = z
  .object({
    notables: WorkspaceConfigAlertSchema,
    operational: WorkspaceConfigAlertSchema,
  })
  .merge(BaseProcessingScheduleSchema)
  .superRefine(...ProcessingScheduleSchemaValidation);

export type WorkspaceConfigAlertsSchemaValues = z.infer<
  typeof WorkspaceConfigAlertsSchema
>;
